import axios from "../../api/apiClient";
import { domain, token } from "../../components/Security";
import { CIVILSTATUS, TAXABLESTATUS } from "../../utils/enums";

import {
  fetchDataForLocation,
  fetchDataForEmployment,
  fetchDataForDepartment,
  fetchDataForSection,
  fetchDataForClassification,
  fetchDataForPaytype,
  fetchDataForShiftSchedule,
  fetchDataForPosition,
  fetchDataForCompany,
  fetchDataForJobTitle,
  fetchDataForEmployeeGroup1,
  fetchDataForEmployeeGroup2,
  fetchDataForPlantilya,
  fetchDataForCostCenter,
  fetchDataForClient,
  fetchDataForEmployeeType,
  fetchDataForVessel,
  fetchDataForDestination,
  fetchSalaryType,
} from "../../components/Personnel/EmployeesListFld/EmployeesListApi";
import maintenanceSalaryCombinationV2Service from "../../v2/services/maintenanceSalaryCombinationV2";

// =============================================================================
// FETCH DATA FOR BANK
// Fetches active banks from the Bank API endpoint.
// Uses /api/Bank/active to only get active banks for dropdowns.
// =============================================================================
const fetchDataForBank = async () => {
  try {
    const response = await axios.get(`${domain}/api/Bank/active`, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching banks:", error);
    return [];
  }
};

// =============================================================================
// FETCH ALL REFERENCE DATA
// Single function that fetches all lookup data needed for employee Excel
// operations — both download template and masterlist.
//
// Returns one `refs` object whose keys match the `refKey` values defined
// in employeeFields.js config. If you add a new dropdown field to the config
// with a new refKey, add the corresponding fetch here.
//
// Enums (civilStatuses, taxableStatuses) are not fetched — they come directly
// from constants so no API call is needed.
//
// Usage:
//   const refs = await fetchAllReferenceData();
//   refs.locations        → [{ id, description, ... }]
//   refs.departments      → [{ id, description, code, ... }]
//   refs.civilStatuses    → ["None", "Single", "Maried"]
//   refs.taxableStatuses  → ["None", "Taxable", "Non-Taxable"]
// =============================================================================
export const fetchAllReferenceData = async () => {
  // Run all async fetches in parallel for performance
  const [
    locations,
    employments,
    departments,
    sections,
    classifications,
    payTypes,
    shiftSchedules,
    positions,
    companies,
    jobTitles,
    employeeGroups1,
    employeeGroups2,
    plantilayas,
    costCenters,
    clients,
    employeeTypes,
    vessels,
    destinations,
    banks,
  ] = await Promise.all([
    fetchDataForLocation(),
    fetchDataForEmployment(),
    fetchDataForDepartment(),
    fetchDataForSection(),
    fetchDataForClassification(),
    fetchDataForPaytype(),
    fetchDataForShiftSchedule(),
    fetchDataForPosition(),
    fetchDataForCompany(),
    fetchDataForJobTitle(),
    fetchDataForEmployeeGroup1(),
    fetchDataForEmployeeGroup2(),
    fetchDataForPlantilya(),
    fetchDataForCostCenter(),
    fetchDataForClient(),
    fetchDataForEmployeeType(),
    fetchDataForVessel(),
    fetchDataForDestination(),
    fetchDataForBank(),
  ]);

  // fetchSalaryType is synchronous — no need to include in Promise.all

  const salaryTypes = fetchSalaryType();

  // Dynamic Salary
  const resMaintenanceSalaryCombinations = await maintenanceSalaryCombinationV2Service.getAll();
  const maintenanceSalaryCombinations = resMaintenanceSalaryCombinations?.data?.rules ?? []; 

  return {
    // --- Async fetched ---
    locations,
    employments,
    departments,
    sections,
    classifications,
    payTypes,
    shiftSchedules,
    positions,
    companies,
    salaryTypes,    // already a flat string array, matches dropdownList usage
    jobTitles,
    employeeGroups1,
    employeeGroups2,
    plantilayas,
    costCenters,
    clients,
    employeeTypes,
    vessels,
    destinations,
    banks,
    maintenanceSalaryCombinations,

    // --- From enums, no fetch needed ---
    // Object.values() converts { SINGLE: "Single", ... } to ["Single", ...]
    civilStatuses:   Object.values(CIVILSTATUS),
    taxableStatuses: Object.values(TAXABLESTATUS),
  };
};